import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Shared UI chrome for landing-page feature preview simulations (ATS, agent search, etc.). */
export function useFeaturePreviewCopy() {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => ({
      atsTitle: t('home.features.items.resume-optimize.title', { defaultValue: "Know your resume's chances before you apply" }),
      agentBrand: t('home.heroPreview.agentProcessingLabel', { defaultValue: 'GoGrabJob Agent' }),
      you: t('home.heroPreview.you', { defaultValue: 'You' }),
      agentRole: t('home.featurePreview.agentRole', { defaultValue: 'Agent' }),
      live: t('home.heroPreview.liveUpdates', { defaultValue: 'Live' }),
      matchLabel: t('home.heroPreview.matchLabel', { defaultValue: 'Match' }),
      userQueryRecommended: t('home.featurePreview.userQueryRecommended', {
        defaultValue: 'What are my recommended jobs?',
      }),
      userYesRewrite: t('home.featurePreview.userYesRewrite', {
        defaultValue: 'Yes, please rewrite it.',
      }),
      pullingRecommended: t('home.featurePreview.pullingRecommended', {
        defaultValue: 'Finding roles that fit your profile…',
      }),
      rolesFoundReply: t('home.featurePreview.rolesFoundReply', {
        defaultValue:
          "Here are 4 roles ranked for your profile. Tap any card on the right — I'll tell you if it's worth pursuing.",
      }),
      recommendedJobs: t('home.featurePreview.recommendedJobs', { defaultValue: 'Recommended jobs' }),
      loading: t('home.featurePreview.loading', { defaultValue: 'Loading…' }),
      waitingRecommendations: t('home.featurePreview.waitingRecommendations', {
        defaultValue: 'Waiting for recommendations…',
      }),
      matchesCount: (count: number) =>
        t('home.featurePreview.matchesCount', {
          defaultValue: '{{count}} matches',
          count,
        }),
      rankedSelectRole: t('home.featurePreview.rankedSelectRole', {
        defaultValue: 'Ranked by profile fit — select a role',
      }),
      rankedClickRole: t('home.featurePreview.rankedClickRole', {
        defaultValue: 'Ranked by profile fit — click any role to optimize or apply.',
      }),
      recommendedForProfile: t('home.featurePreview.recommendedForProfile', {
        defaultValue: 'Recommended for your profile',
      }),
      waitingAgentResults: t('home.featurePreview.waitingAgentResults', {
        defaultValue: 'Waiting for agent results…',
      }),
      scanning: t('home.featurePreview.scanning', { defaultValue: 'Scanning…' }),
      badMatchReply: t('home.featurePreview.badMatchReply', {
        defaultValue:
          "Not a strong match — only 58% fit for Staff PM. Skill gaps in compliance frameworks and platform vision. I'd skip this one.",
      }),
      rewriteAskReply: t('home.featurePreview.rewriteAskReply', {
        defaultValue:
          '91% match for Senior PM at Microsoft — your roadmap + B2B SaaS experience aligns well. Want me to rewrite your resume for this role?',
      }),
      openingCanvasReply: t('home.featurePreview.openingCanvasReply', {
        defaultValue:
          'Opening optimize canvas — scoring your resume against the live JD and rewriting sections for ATS alignment.',
      }),
      promptHintAgent: t('home.featurePreview.promptHintAgent', {
        defaultValue: 'Ask agent to score, rewrite, or apply for any role…',
      }),
      agentSearchQuery: t('home.heroPreview.agentQuery', {
        defaultValue:
          'Find senior product manager roles that match my resume and are remote-friendly.',
      }),
    }),
    [t, i18n.language],
  );
}
